import { Field } from 'formik';
import { Fade, TextField } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

interface Props {
    name: string;
    label: string;
    placeholder?: string;
    type?: string;
    required?: boolean;
    disabled?: boolean;
    /** Formik's `touched[name]` and `errors[name]`, and the current value. */
    touched?: boolean;
    error?: string;
    value?: string;
}

/**
 * A text field that validates late and forgives early.
 *
 * Nothing is marked wrong until the user has left the field, so typing a name no longer lights up
 * the two fields below it with "required". Once a field is in error it re-checks on every
 * keystroke, so the message clears the moment it is fixed rather than at the next blur.
 *
 * A field that is filled, left, and valid gets a small green check. It is the only decoration on
 * these inputs, and unlike the icons that used to sit here it carries state rather than repeating
 * the label. It only ever renders when there is a value, so the shrunk label it forces is correct.
 */
export const ValidatedTextField = ({
    name,
    label,
    placeholder,
    type,
    required,
    disabled,
    touched,
    error,
    value,
}: Props) => {
    const showError = Boolean(touched && error);
    const showValid = Boolean(touched && !error && value);

    return (
        <Field
            as={TextField}
            name={name}
            label={label}
            placeholder={placeholder}
            type={type}
            variant="outlined"
            fullWidth
            required={required}
            disabled={disabled}
            error={showError}
            helperText={showError ? error : ' '}
            slotProps={{
                // `helperText` is always rendered, as a space when there is no message, so the
                // field below does not jump down the page when an error appears.
                formHelperText: { sx: { minHeight: '1.25em' } },
                input: {
                    endAdornment: (
                        <Fade in={showValid}>
                            <CheckCircleRoundedIcon
                                aria-hidden="true"
                                sx={{ fontSize: 20, color: 'success.main' }}
                            />
                        </Fade>
                    ),
                },
            }}
        />
    );
};

export default ValidatedTextField;
